/**
 * Build absolute API URL
 * Ensures API calls work correctly regardless of homepage setting
 * Always resolves from domain root, not relative to homepage
 */
export function apiUrl(path) {
  // Ensure path starts with /api/
  if (!path.startsWith('/api/')) {
    path = path.startsWith('/') ? path : `/${path}`;
    if (!path.startsWith('/api/')) {
      path = `/api${path.startsWith('/') ? '' : '/'}${path}`;
    }
  }

  // Build absolute URL from origin to bypass homepage setting
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }

  // Fallback for SSR
  return path;
}

// Cuando Cloudflare Access expira la sesión devuelve 302 → cameramarket.cloudflareaccess.com.
// Si fetch sigue el redirect cross-origin, el navegador lo bloquea por CORS y tira
// `TypeError: Failed to fetch`, perdiendo el dato que el usuario estaba escribiendo.
// Solución: detectamos el redirect y lanzamos SessionExpiredError, la UI ofrece
// "Recargar" sin perder lo escrito.
export class SessionExpiredError extends Error {
  constructor() {
    super('Tu sesión de Cloudflare Access expiró. Recarga para continuar.');
    this.name = 'SessionExpiredError';
    this.sessionExpired = true;
  }
}

// Bus mínimo para que cualquier apiFetch que tope con expiry dispare el banner
// global sin tener que pasar la callback por todo el árbol de componentes.
const sessionExpiredListeners = new Set();
export function onSessionExpired(cb) {
  sessionExpiredListeners.add(cb);
  return () => sessionExpiredListeners.delete(cb);
}
function notifySessionExpired() {
  for (const cb of sessionExpiredListeners) { try { cb(); } catch {} }
}

function isSessionExpiredResponse(response) {
  // redirect:'manual' devuelve un Response opaque (type='opaqueredirect', status=0)
  // cuando el server emite 3xx. Es la huella inequívoca del bounce a CF Access.
  return response.type === 'opaqueredirect' || response.status === 0;
}

// Probe definitivo: ¿de verdad expiró la sesión de CF Access, o fue un blip de red?
// Hace hasta `attempts` pings sondeo a /api/health:
//   · opaqueredirect / status 0  → CF bounce real           → true
//   · respuesta del server (ok o 5xx) → sesión viva          → false (cancela)
//   · failed-to-fetch / error de red  → inconcluso, reintenta
// Sólo devuelve true ante señal inequívoca; así los cortes transitorios de red
// dejan de disparar el banner "recarga la página" (causa #1 de que saliera tanto).
let _confirmInFlight = null;
async function confirmSessionExpired({ attempts = 2, gapMs = 1500 } = {}) {
  if (_confirmInFlight) return _confirmInFlight;
  _confirmInFlight = (async () => {
    for (let i = 0; i < attempts; i++) {
      try {
        const res = await fetch(apiUrl('/api/health'), {
          credentials: 'include',
          redirect: 'manual',
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (isSessionExpiredResponse(res)) return true;   // CF bounce confirmado
        return false;                                      // el server respondió → sesión viva
      } catch (_) {
        // inconcluso (red/CORS): reintentamos una vez antes de rendirnos
      }
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, gapMs));
    }
    return false; // sin señal inequívoca → no molestamos al usuario
  })();
  try {
    return await _confirmInFlight;
  } finally {
    _confirmInFlight = null;
  }
}

/**
 * Fetch wrapper with proper API URL resolution and auth
 */
export async function apiFetch(path, options = {}) {
  const url = apiUrl(path);

  const defaultHeaders = {
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    ...options.headers,
  };

  // Auth de operador global: el backend exige X-Operator-Secret en TODAS las rutas
  // de operador (/api/*, salvo la allowlist pública del gate). Antes solo lo mandaba
  // opFetch en un par de pestañas; el resto del dashboard usa apiFetch directo, así
  // que lo adjuntamos aquí para las ~58 pantallas. SOLO si el build tiene el secreto
  // inyectado (REACT_APP_SELL_OPERATOR_SECRET): el build del sitio PÚBLICO (/vende,
  // /tasa, /objetivos) NO define esa env → nunca envía el secreto ni lo hornea en su
  // bundle. Respetamos un header ya puesto por el caller (opFetch) para no pisarlo.
  const OP_SECRET = process.env.REACT_APP_SELL_OPERATOR_SECRET;
  if (OP_SECRET && !defaultHeaders['X-Operator-Secret'] && !defaultHeaders['x-operator-secret']) {
    defaultHeaders['X-Operator-Secret'] = OP_SECRET;
  }

  // Add Content-Type when sending a body. The previous check
  // (`typeof body === 'object'`) failed for already-stringified JSON
  // (very common pattern: `body: JSON.stringify(...)`) because the
  // typeof string isn't 'object'. Without the header Express.json()
  // silently discards the body and the handler sees `req.body = {}`
  // — every PATCH/POST became a no-op (saw it in purchases save).
  if (options.body && !(options.body instanceof FormData) && !defaultHeaders['Content-Type']) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  const defaultOptions = {
    credentials: 'include',
    redirect: 'manual',   // ver SessionExpiredError arriba
    headers: defaultHeaders,
  };

  let response;
  try {
    // `headers: defaultHeaders` va DESPUÉS de `...options` a propósito: options
    // suele traer su propio `headers` (p.ej. opFetch añade X-Operator-Secret) que,
    // si se dejara ganar al spread, MACHACABA los headers ya fusionados y borraba el
    // Content-Type recién puesto → express.json() no parseaba el body → req.body={} →
    // toda mutación de operador con body era un no-op silencioso (200 en falso).
    // defaultHeaders ya incluye ...options.headers, así que no se pierde nada.
    response = await fetch(url, { ...defaultOptions, ...options, headers: defaultHeaders });
  } catch (e) {
    // "Failed to fetch" estando online PUEDE ser CF Access (algunos navegadores
    // rechazan en vez de devolver opaqueredirect)… pero también un blip de red
    // normal. Antes lo tratábamos siempre como expiry → el banner saltaba por
    // cualquier corte transitorio. Ahora confirmamos en segundo plano y sólo
    // disparamos el banner si el sondeo ve el bounce real a CF.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) throw e;
    if (e && e.name === 'TypeError' && /failed to fetch/i.test(String(e.message || ''))) {
      confirmSessionExpired().then((dead) => { if (dead) notifySessionExpired(); });
      const netErr = new Error('Conexión interrumpida. Reintenta en un momento.');
      netErr.transient = true;
      throw netErr;   // la promise rechaza → el editor inline NO se cierra, no se pierde lo escrito
    }
    throw e;
  }

  if (isSessionExpiredResponse(response)) {
    notifySessionExpired();
    throw new SessionExpiredError();
  }

  if (!response.ok) {
    // Try to surface the server-provided error message (JSON body or text)
    // so the dashboard shows "QUOTA_EXCEEDED" / "missing_credentials" instead
    // of a bare "4xx ..." that operators have to grep the logs for.
    let detail = '';
    try {
      const ct = response.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        const j = await response.clone().json();
        detail = j?.error?.message || j?.error?.code || j?.error || j?.message || '';
        if (typeof detail !== 'string') detail = JSON.stringify(detail).slice(0, 200);
      } else {
        const t = await response.clone().text();
        if (t) detail = t.slice(0, 200);
      }
    } catch {}
    const err = new Error(`API ${response.status} ${response.statusText}${detail ? ` — ${detail}` : ''}`);
    err.status = response.status;
    err.detail = detail || null;
    throw err;
  }

  // Only parse JSON if there's content
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  return response;
}

// ─── Cargas de recursos protegidos (img/descarga/abrir) ───
// El gate de operador exige el header X-Operator-Secret en TODO /api (salvo la
// allowlist pública). Un <img src>, un <a href> o window.location NO pueden mandar
// headers → el gate fail-closed los corta con 401 (adjuntos, etiquetas, POD, CSV…).
// Estos helpers traen el recurso por apiFetch (que SÍ adjunta el secreto), lo pasan
// a object URL y disparan la acción. Úsalos en vez de meter la ruta cruda en el DOM.

// Trae una ruta /api como blob y devuelve un object URL (el caller lo revoca).
export async function apiBlobUrl(path) {
  const res = await apiFetch(path);            // lanza en !ok / sesión expirada
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// Abre el recurso en una pestaña nueva (equivalente autenticado de <a target=_blank>).
export async function apiOpen(path) {
  const url = await apiBlobUrl(path);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Descarga el recurso con nombre de archivo (equivalente autenticado de <a download>).
export async function apiDownload(path, filename) {
  const url = await apiBlobUrl(path);
  const a = document.createElement('a');
  a.href = url; a.download = filename || 'descarga';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

// ─── CF Access session heartbeat ───
// Mantiene la cookie de CF Access fresca mientras el dashboard esté abierto.
// CF Access tiene rolling session: cualquier request autenticado renueva el TTL.
// Pingeamos /api/health cada 4 min para que la sesión nunca caduque mientras
// la pestaña esté viva. Si el ping detecta SessionExpiredError dispara el
// listener `onExpired` para que la UI muestre el aviso.
let heartbeatTimer = null;

export function startSessionHeartbeat({ intervalMs = 4 * 60 * 1000, onExpired } = {}) {
  if (typeof window === 'undefined') return () => {};
  if (heartbeatTimer) return () => {};
  let expired = false;
  const tick = async () => {
    if (expired || document.hidden) return;   // pestaña en background o ya expirado: no gastamos invocaciones
    try {
      await apiFetch('/api/health');
    } catch (e) {
      if (e && e.sessionExpired) {
        expired = true;
        if (typeof onExpired === 'function') { try { onExpired(); } catch {} }
      }
      // Otros errores (500, red) los ignoramos: el ping solo sirve para keepalive.
    }
  };
  heartbeatTimer = window.setInterval(tick, intervalMs);
  // Primer tick inmediato al volver a la pestaña — si la sesión expiró durante
  // un suspend largo lo detectamos antes de que el usuario teclee algo.
  const onVisible = () => { if (!document.hidden) tick(); };
  document.addEventListener('visibilitychange', onVisible);
  return () => {
    window.clearInterval(heartbeatTimer);
    heartbeatTimer = null;
    document.removeEventListener('visibilitychange', onVisible);
  };
}
